<template>
  <Container hideFooter title="Visitor sign-out">
    <template v-slot:body>
      <div class="flow-layout signout-flow">
        <section class="flow-intro">
          <div class="step-badge step-badge--accent">
            <span>01</span><i></i><span>VISITOR</span>
          </div>
          <h1>Heading<br /><span>out?</span></h1>
          <p class="flow-lede">
            Let us know you’re leaving so your host knows you’ve wrapped up your
            visit.
          </p>

          <div class="signout-art" aria-hidden="true">
            <div class="door-frame">
              <div class="door"><span></span></div>
            </div>
            <div class="door-arrow">
              <IconGlyph name="arrow-right" :size="24" />
            </div>
            <div class="door-sun"></div>
            <span class="signout-art-label">SEE YOU<br />NEXT TIME</span>
          </div>

          <div class="trust-row">
            <span class="trust-icon"><IconGlyph name="check" :size="20"/></span>
            <span
              ><strong>Thanks for stopping by</strong
              ><small>We hope to see you again soon.</small></span
            >
          </div>
        </section>

        <section
          class="form-card signout-card"
          aria-label="Visitor sign-out form"
        >
          <div class="form-card-heading">
            <div>
              <span class="section-overline">Almost done</span>
              <h2>Find your visit</h2>
            </div>
            <span class="required-note"><span>*</span> Required</span>
          </div>

          <form @submit.prevent="signOut">
            <div class="field-group">
              <label class="field-label" for="guest-name"
                >Your name <span>*</span></label
              >
              <div class="select-wrap">
                <select
                  id="guest-name"
                  v-model="guest"
                  class="kiosk-select"
                  :disabled="loading || !guests.length"
                >
                  <option :value="null" disabled>{{
                    guests.length ? "Select your name" : "Loading visitors…"
                  }}</option>
                  <option
                    v-for="item in guests"
                    :key="item._id"
                    :value="item"
                    >{{ item.name }}</option
                  >
                </select>
                <IconGlyph name="chevron-down" :size="18" />
              </div>
              <span v-if="!loading && !guests.length" class="empty-state"
                >No active visitors to sign out right now.</span
              >
            </div>

            <div class="signout-confirm-note">
              <span class="confirm-note-icon"
                ><IconGlyph name="lock" :size="18"
              /></span>
              <span
                ><strong>Private &amp; simple</strong
                ><small>Your visit will be marked complete.</small></span
              >
            </div>

            <div class="form-card-bottom">
              <span class="form-hint"
                ><span class="hint-dot"></span> This helps us keep an accurate
                visitor list.</span
              >
              <button
                class="primary-cta"
                name="signin"
                type="submit"
                :disabled="loading || !guest"
              >
                <span>{{ loading ? "Signing out…" : "Sign me out" }}</span>
                <IconGlyph v-if="!loading" name="arrow-right" :size="20" />
              </button>
            </div>
          </form>
        </section>
      </div>
    </template>
  </Container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Container from "@/components/Container";
import IconGlyph from "@/components/IconGlyph";

export default {
  name: "SignOut",
  components: { Container, IconGlyph },
  data() {
    return {
      guest: null,
      guests: [],
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 500,
        type: "guest",
        sortBy: ["date"],
        sortDesc: [true],
        signedIn: true
      }
    };
  },
  computed: {
    ...mapGetters({ signedInGuests: "getHistory" })
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    ...mapActions({ getHistory: "getHistory", signOutUser: "updateUser" }),
    async signOut() {
      if (!this.guest) return;
      this.loading = true;
      try {
        const visitor = { ...this.guest, signedIn: false, dateOut: Date.now() };
        await this.signOutUser(visitor);
        await this.loadData();
      } finally {
        this.loading = false;
      }
    },
    async loadData() {
      this.guest = null;
      await this.getHistory(this.options);
      this.guests = this.signedInGuests.docs || [];
    }
  }
};
</script>

<style>
.signout-flow .flow-intro h1 span {
  color: var(--accent);
}

.signout-art {
  position: relative;
  width: 100%;
  height: 152px;
  margin: 33px 0 28px;
  overflow: hidden;
  border-radius: 21px;
  background: #f8e4d7;
}

.door-frame {
  position: absolute;
  bottom: 0;
  left: 78px;
  width: 88px;
  height: 125px;
  border: 6px solid var(--orange);
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
}

.door {
  position: absolute;
  bottom: 0;
  left: 12px;
  width: 59px;
  height: 112px;
  border-radius: 3px 3px 0 0;
  background: #f6bc7b;
}

.door::before,
.door::after {
  position: absolute;
  left: 11px;
  width: 37px;
  height: 1px;
  background: rgba(201, 95, 45, 0.2);
  content: "";
}

.door::before {
  top: 35px;
}
.door::after {
  top: 73px;
}

.door span {
  position: absolute;
  top: 56px;
  right: 7px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--orange);
}

.door-arrow {
  position: absolute;
  bottom: 34px;
  left: 199px;
  display: grid;
  width: 43px;
  height: 43px;
  border-radius: 50%;
  background: var(--accent);
  color: white;
  place-items: center;
}

.door-sun {
  position: absolute;
  top: -48px;
  right: -7px;
  width: 165px;
  height: 165px;
  border: 1px solid rgba(233, 86, 53, 0.24);
  border-radius: 50%;
}

.signout-art-label {
  position: absolute;
  top: 44px;
  left: 286px;
  color: var(--orange);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.17em;
  line-height: 1.45;
}

.signout-confirm-note {
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 61px;
  margin: 28px 0 4px;
  padding: 0 15px;
  border-radius: 13px;
  background: #f3f1ed;
}

.confirm-note-icon {
  display: grid;
  width: 35px;
  height: 35px;
  border-radius: 10px;
  background: var(--accent-soft);
  color: var(--accent);
  place-items: center;
}

.signout-confirm-note span:last-child {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.signout-confirm-note strong {
  color: var(--ink);
  font-size: 11px;
}

.signout-confirm-note small,
.empty-state {
  color: var(--muted);
  font-size: 10px;
}

.empty-state {
  display: block;
  margin: 10px 0 0;
}

.kiosk-select:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

@media (max-width: 620px) {
  .signout-confirm-note {
    margin-top: 18px;
  }
}
</style>
